import { DisplayAdsManagerProvider } from '@tunein/display-ads/react';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { api } from 'src/client/api';
import { displayAds } from 'src/client/displayAds';
import { DISPLAY_ADS_LIBRARY_ENABLED } from 'src/common/constants/experiments/ads';
import useExperiment from 'src/common/hooks/useExperiment';

export default function DisplayAdsProvider({
  children,
  locale,
  routeProps,
}: {
  children: ReactNode;
  locale?: string;
  routeProps?: { hideAds?: boolean };
}) {
  const [didInitialize, setDidInitialize] = useState(false);
  const isDisplayAdsLibraryEnabled = useExperiment(DISPLAY_ADS_LIBRARY_ENABLED, false);
  const shouldEnableDisplayAds = isDisplayAdsLibraryEnabled && !routeProps?.hideAds;

  useEffect(() => {
    if (!shouldEnableDisplayAds) {
      setDidInitialize(false);
      return;
    }

    let isCancelled = false;
    let isInitializing = false;

    const initDisplayAds = () => {
      if (displayAds.isReady) {
        setDidInitialize(true);
        return true;
      }

      if (isInitializing) {
        return false;
      }

      isInitializing = true;
      const didInit = displayAds.init({ locale });
      isInitializing = false;

      if (!isCancelled) {
        setDidInitialize(true);
      }

      return isCancelled || didInit;
    };

    const onUserData = () => {
      if (initDisplayAds()) {
        api.off('userData', onUserData);
      }
    };

    if (!initDisplayAds()) {
      api.on('userData', onUserData);
    }

    return () => {
      isCancelled = true;
      api.off('userData', onUserData);
    };
  }, [locale, shouldEnableDisplayAds]);

  return (
    <DisplayAdsManagerProvider displayAdsManager={didInitialize ? displayAds : null}>
      {children}
    </DisplayAdsManagerProvider>
  );
}
